import {EntityMinMaxConfig} from 'Quoting/models/DTOs/QuoteForm';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {createUniqueFormFieldId, getByKeyPath, isDefined} from 'Quoting/utils/utilities';
import {List as IList, Map as IMap} from 'immutable';
import {FieldValidator} from '../components/QuoteForm/Plan/products/types';
import {getValidators} from './validationHelpers';

type ValidationParams = {
  value: any;
  label?: string;
  entityMinMaxConfig: EntityMinMaxConfig[0];
  coverageName: string;
};

const DELIMITER = '.';

export const extractFormFieldIdDetails = (formFieldId: string) => {
  const coverageCode = formFieldId?.split(DELIMITER)?.[1];
  const entityId = formFieldId?.split(DELIMITER)?.[2];
  const attributeIdWithPlanKey = formFieldId?.split(DELIMITER)?.[3];
  const attributeId = attributeIdWithPlanKey?.split('__')?.[0];

  return {coverageCode, entityId, attributeId};
};

export const validateFieldMinMaxConstraints = (validationParams: ValidationParams) => {
  const {value, label, entityMinMaxConfig, coverageName} = validationParams;

  const {min, max, increment} = entityMinMaxConfig || {};

  let error = false;
  let helperText = '';

  if (
    (isDefined(increment) && (value - (min ?? 0)) % increment !== 0) ||
    (isDefined(min) && value < min) ||
    (isDefined(max) && value > max)
  ) {
    error = true;
    helperText = `${label} under ${coverageName} must be between ${min} and ${max} with increments of ${increment}:minMax`;
  }

  return {error, helperText};
};

export const runProductValidations = (
  quote: IMap<string, any>,
  productModelName: A360ProductModel,
  minMaxConfig?: Record<string, EntityMinMaxConfig>
) => {
  const errors = {};
  const validators = getValidators(productModelName, minMaxConfig);
  if (IMap.isMap(quote) && validators) {
    const plans = getByKeyPath(quote, 'benefitPackagingDetail.plans') as IList<any>;
    plans?.forEach((plan: IMap<string, any>) => {
      const planKey = getByKeyPath(plan, '_key.id') as string;
      const coverageDefs = getByKeyPath(plan, 'coverageDefs') as IList<any>;
      coverageDefs?.forEach((coverage: IMap<string, any>) => {
        validators?.coverageFieldValidators?.[coverage?.get('code')]?.forEach(validateRule(coverage, errors, planKey));
        validators?.minMaxValidators?.[coverage?.get('code')]?.forEach(validateRule(coverage, errors, planKey));
      });

      validators?.planFieldValidators?.forEach(validateRule(plan, errors, planKey));
    });

    validators?.quoteFieldValidators?.forEach(validateRule(quote, errors));
  }
  return errors;
};

const validateRule = (
  input: Parameters<FieldValidator['validate']>[0],
  errors: Record<string, string>,
  planKey?: string
) => (rule: FieldValidator) => {
  const validationResult = rule.validate(input);

  if (!validationResult) return;

  const uniqueErrorKey = createUniqueFormFieldId(validationResult?.formFieldId, planKey);

  if (errors[uniqueErrorKey]) return; // set only first instance of error (since multiple validators for same field might exist)

  if (isDefined(validationResult) && !errors[uniqueErrorKey]) {
    errors[uniqueErrorKey] = validationResult?.errorMessage;
  } else {
    errors[uniqueErrorKey] = undefined;
  }
};
